import { PAIN_POSTS } from "@/lib/posts-pain";
import { POSTURE_POSTS } from "@/lib/posts-posture";
import { RMT_POSTS } from "@/lib/posts-rmt";
import { SPORTS_POSTS } from "@/lib/posts-sports";
import { HEAD_POSTS } from "@/lib/posts-head";
import { RELAX_POSTS } from "@/lib/posts-relax";
import { PRENATAL_POSTS } from "@/lib/posts-prenatal";
import { THAI_POSTS } from "@/lib/posts-thai";

const bimg = (id, crop = "entropy", w = 1400) =>
  `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop&crop=${crop}`;

const FIRST_POSTS = [
  {
    slug: "deep-tissue-massage-edmonton",
    title: "Deep Tissue Massage in Edmonton: What It Is and Who It Helps",
    keyword: "deep tissue massage Edmonton",
    category: "Deep Tissue & Chronic Pain",
    read: "5 min read",
    img: bimg("photo-1519824145371-296894a0daa9"),
    excerpt:
      "Slow, sustained pressure that reaches the deeper layers of muscle and fascia — who it suits, what it feels like, and when to choose something gentler.",
    body: [
      {
        p: [
          "Deep tissue massage is a therapeutic technique built around slow, deliberate strokes and sustained pressure. Instead of gliding over the surface, the therapist works gradually into the deeper layers of muscle and the connective tissue — fascia — that surrounds it.",
          "At Royal Essence Massage in Edmonton, deep tissue work is never a fixed routine. Pressure, pace and focus areas are adjusted to the person on the table that day.",
        ],
      },
      {
        h: "What makes it “deep”",
        p: [
          "The depth comes from patience, not force. The therapist warms the superficial layers first, then uses forearms, elbows, knuckles and thumbs to follow the grain of the muscle, soften adhesions and release trigger points — the small, irritable knots that can refer pain elsewhere.",
          "A good deep tissue session should feel like “productive discomfort” at most — never sharp or bracing pain. You should always be able to breathe through the pressure.",
        ],
      },
      {
        h: "Who it tends to suit",
        p: [
          "People with long-standing tension in the back, neck and shoulders; desk workers with posture-related tightness; and active people whose training leaves muscles dense and shortened.",
          "If you are new to massage, very sensitive to pressure, or simply exhausted, a Swedish relaxation session is often the kinder starting point — deep tissue can come later.",
        ],
      },
    ],
    note:
      "Please check with your physician before deep tissue work if you are pregnant, have osteoporosis, a recent surgery or injury, or a blood-clotting condition. Massage may help reduce symptoms, but it does not replace medical care.",
  },
  {
    slug: "deep-tissue-vs-swedish-massage",
    title: "Deep Tissue vs. Swedish Massage: How to Choose",
    keyword: "deep tissue vs Swedish massage",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: bimg("photo-1519823551278-64ac92734fb1"),
    excerpt:
      "Two classics, two different intentions. A simple way to decide which one your body is actually asking for.",
    body: [
      {
        p: [
          "Swedish and deep tissue massage use many of the same strokes — the difference is intention, depth and pace. Swedish massage is the art of unwinding: long, flowing movements with light-to-medium pressure that calm the nervous system and ease general tension.",
          "Deep tissue is the art of change: slower, focused pressure aimed at specific layers of muscle and fascia where tension has been living for months or years.",
        ],
      },
      {
        h: "Choose deep tissue when…",
        p: [
          "You can point to where it hurts. A stubborn knot between the shoulder blades, a hip that never quite loosens, a neck that has forgotten its range of motion — these are deep tissue questions.",
        ],
      },
      {
        h: "Choose Swedish when…",
        p: [
          "You are tired rather than injured. If the week has been heavy, sleep is short and your whole system feels “on”, flowing relaxation work will usually do more for you than intense pressure.",
          "Many clients alternate: a Swedish session to exhale, a deep tissue session to change something specific. At Royal Essence we often blend both within one appointment.",
        ],
      },
    ],
    note:
      "Not sure which to book? Book either — we decide together during the consultation at the start of your session.",
  },
  {
    slug: "deep-tissue-massage-for-back-pain",
    title: "Can Deep Tissue Massage Help Chronic Back Pain?",
    keyword: "deep tissue massage for back pain",
    category: "Deep Tissue & Chronic Pain",
    read: "5 min read",
    img: bimg("photo-1745327883508-b6cd32e5dde5"),
    excerpt:
      "What research and clinical experience suggest about deep tissue work for long-standing back pain — and what realistic expectations look like.",
    body: [
      {
        p: [
          "Chronic back pain is rarely only about the back. It is shaped by how you sit, sleep, work, train and carry stress. Massage cannot rewrite all of that in an hour — but it can change the part that lives in the tissue.",
          "Research on massage for chronic low back pain generally shows meaningful short-term relief of pain and stiffness, especially with a series of sessions rather than a single visit.",
        ],
      },
      {
        h: "How deep tissue sessions help",
        p: [
          "Slow, sustained pressure softens guarded muscles, improves local circulation and releases trigger points that keep the back in a protective spasm. Just as important, the surrounding cast — glutes, hips, hip flexors and hamstrings — is included, because the lower back rarely acts alone.",
        ],
      },
      {
        h: "What realistic progress looks like",
        p: [
          "Most people feel looser after the first session. Lasting change for long-standing pain usually takes a short course — often 3 to 6 sessions spaced a week or two apart — combined with small daily habits we discuss together.",
        ],
      },
    ],
    note:
      "See a physician first if your back pain comes with numbness, weakness down the leg, bladder or bowel changes, fever, or follows a fall or accident. Massage may help reduce symptoms — it does not replace medical diagnosis.",
  },
  {
    slug: "massage-for-lower-back-pain",
    title: "Massage for Lower Back Pain: Techniques a Therapist Uses",
    keyword: "massage for lower back pain",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: bimg("photo-1519824145371-296894a0daa9", "entropy"),
    excerpt:
      "Why the lower back tightens, which techniques actually reach it, and why the hips and glutes are always part of the conversation.",
    body: [
      {
        p: [
          "The lower back is a crossroads: everything you do with your upper body passes through it, and everything your legs do pushes up into it. Hours of sitting, lifting, driving or standing all write themselves into this small region.",
        ],
      },
      {
        h: "Techniques that reach it",
        p: [
          "A typical session may combine slow deep tissue strokes along the erector muscles, myofascial release for the tissue around the pelvis, acupressure on stubborn points, and — when appropriate — moving cupping or hot stones to bring warmth and blood flow into dense areas.",
        ],
      },
      {
        h: "Why your hips and glutes are included",
        p: [
          "Tight glutes and hip flexors tilt the pelvis and make the lower back work overtime. Releasing them often reduces back discomfort more than working the back alone. This is why a session with me never follows a template — the lower back tells one story, but the body tells the whole one.",
        ],
      },
    ],
    note:
      "If lower back pain is sharp, radiating, or followed an injury, check with your physician before booking. Massage works best as a complement to — not a substitute for — medical care.",
  },
  {
    slug: "massage-for-pain-between-shoulder-blades",
    title: "Pain Between the Shoulder Blades: Causes and How Massage Helps",
    keyword: "massage for pain between shoulder blades",
    category: "Deep Tissue & Chronic Pain",
    read: "4 min read",
    img: bimg("photo-1519823551278-64ac92734fb1", "entropy"),
    excerpt:
      "That familiar burn between the shoulder blades is usually a posture story. Here is what is behind it and what a session looks like.",
    body: [
      {
        p: [
          "The ache or burning between the shoulder blades is one of the most common complaints among people who work at a desk or drive for long hours. The muscles between the blades — the rhomboids and middle trapezius — spend all day holding your shoulders forward against gravity. They are not weak; they are overworked.",
        ],
      },
      {
        h: "What a session focuses on",
        p: [
          "Releasing the overworked rhomboids and traps is only half of the work. The chest muscles at the front — which pull the shoulders forward in the first place — are gently opened as well, along with the neck and the space under the shoulder blade itself.",
          "Expect slow, specific pressure and a lot of small “there it is” moments.",
        ],
      },
      {
        h: "What helps between sessions",
        p: [
          "Short posture resets during the day matter more than perfect posture: stand, roll the shoulders back, let the chest open for thirty seconds. Little and often beats long and rare.",
        ],
      },
    ],
    note:
      "Pain between the shoulder blades that is sudden, severe, or comes with chest pressure or shortness of breath needs medical attention first — not massage.",
  },
  {
    slug: "massage-for-neck-pain",
    title: "Massage for Neck Pain: When to See a Massage Therapist",
    keyword: "massage for neck pain",
    category: "Back, Neck, Shoulders & Posture",
    read: "4 min read",
    img: bimg("photo-1596178060671-7a80dc8059ea"),
    excerpt:
      "Screens, stress and sleep positions all write themselves into the neck. When massage helps — and when it is a doctor question.",
    body: [
      {
        p: [
          "The neck holds a heavy head all day while you look down at screens, clench through stress and sleep in strange positions. No wonder it complains. Most everyday neck tension responds well to skilled, patient bodywork.",
        ],
      },
      {
        h: "How massage may help",
        p: [
          "A neck session works the full support system: the suboccipital muscles at the base of the skull, the levator scapulae along the side of the neck, the upper traps and the jaw. Pressure here is precise and gentle — the neck does not respond to force, it responds to patience.",
          "Many clients notice not just easier movement, but quieter tension headaches and better sleep afterwards.",
        ],
      },
    ],
    note:
      "See a physician before massage if neck pain follows an accident, comes with numbness or tingling into the arm or hand, or arrives with a severe, unusual headache.",
  },
  {
    slug: "best-massage-for-back-pain",
    title: "The Best Massage for Back Pain: A Therapist’s Honest Guide",
    keyword: "best massage for back pain",
    category: "Back, Neck, Shoulders & Posture",
    read: "4 min read",
    img: bimg("photo-1745327883508-b6cd32e5dde5", "entropy"),
    excerpt:
      "There is no single “best” massage for back pain — but there is a best one for your back, this week. How to think about the choice.",
    body: [
      {
        p: [
          "If you search for the best massage for back pain, you will find confident rankings. The honest answer is less tidy: the right technique depends on whether your pain is fresh or long-standing, sharp or dull, local or radiating — and on how your body responds to pressure.",
        ],
      },
      {
        h: "A simple map",
        p: [
          "Long-standing, specific tightness (a knot you can point to) — deep tissue or customized therapeutic massage. General heaviness and stress-related ache — Swedish relaxation. A body that craves warmth and comfort — hot stone. Unsure — a customized session that blends all of them.",
        ],
      },
      {
        h: "Why the consultation matters",
        p: [
          "The most therapeutic part of a session often happens before the massage begins: a short conversation about how you sleep, sit and work. That is what turns a generic routine into a treatment built for your back, on that day.",
        ],
      },
    ],
    note:
      "Whichever style you choose, massage may help reduce pain and stiffness — but persistent, worsening or radiating back pain deserves a medical evaluation.",
  },
  {
    slug: "massage-for-shoulder-tension",
    title: "Massage for Shoulder and Trapezius Tension",
    keyword: "massage for shoulder tension",
    category: "Back, Neck, Shoulders & Posture",
    read: "4 min read",
    img: bimg("photo-1596178060671-7a80dc8059ea", "top"),
    excerpt:
      "Why the shoulders creep up toward the ears, what a therapist actually does about it, and how to keep them down between sessions.",
    body: [
      {
        p: [
          "Notice your shoulders right now. If they are anywhere near your ears, you are in good company. The trapezius muscles lift the shoulders in response to concentration and stress — and for many people they simply never come back down.",
        ],
      },
      {
        h: "What a session looks like",
        p: [
          "Work begins broad and slow — warming the upper back and traps — then narrows to the specific fibres that hold the tension: the upper traps, the levator scapulae, the small rotator cuff muscles and the front of the shoulder, where desk posture anchors everything forward.",
        ],
      },
      {
        h: "Keeping the change",
        p: [
          "Massage creates the release; small habits keep it. A screen at eye level, a keyboard close to the body, and a sixty-second shoulder reset a few times a day will hold the results far longer than willpower.",
        ],
      },
    ],
    note:
      "Shoulder pain with weakness, night pain that wakes you, or pain after a fall should be checked by a physician before massage.",
  },
];

export const POSTS = [
  ...FIRST_POSTS,
  ...PAIN_POSTS,
  ...POSTURE_POSTS,
  ...RMT_POSTS,
  ...SPORTS_POSTS,
  ...HEAD_POSTS,
  ...RELAX_POSTS,
  ...PRENATAL_POSTS,
  ...THAI_POSTS,
];

export const CATEGORIES = [...new Set(POSTS.map((p) => p.category))];
